import { useState, useEffect } from 'react';
import { captureException } from '@sentry/react';
import { doc, type FirestoreError, onSnapshot } from '@firebase/firestore';

import { db } from '../firebase-config';
import type { GeneralPatientInfo } from '../types/generalPatientInfo.temp.types';

type UseGetPatientDataState = {
  data: GeneralPatientInfo | null;
  isLoading: boolean;
  error: FirestoreError | null;
};

type UseGetPatientDataParams = {
  patientId: string;
};

export const useGetPatientData = ({ patientId }: UseGetPatientDataParams) => {
  const [state, setState] = useState<UseGetPatientDataState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'patients', patientId),
      (document) => {
        const data = { ...document.data(), id: document.id } as GeneralPatientInfo;
        setState({ data, isLoading: false, error: null });
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('Error fetching patient data:', error);
        }

        captureException(error, {
          tags: { issueIn: 'useGetPatientData' },
          extra: { patientId },
        });

        setState({ data: null, isLoading: false, error });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [patientId]);

  return { data: state.data, isLoading: state.isLoading, error: state.error } as const;
};
